import React, { useState, useEffect, useRef } from 'react';
import { Auth, API } from '../../utils/amplifyWrapper';
import { toast } from 'react-toastify';
import { Check, Eye, EyeOff } from 'lucide-react';
import * as Sentry from '@sentry/react';
import { C } from '../../styles/tokens';
import ProfileSkeleton from '../../components/skeletons/pages/ProfileSkeleton';

// ── Layout helpers ────────────────────────────────────────────────────────────

function PageShell({ title, breadcrumb, children }) {
    return (
        <div style={{ flex: 1, overflowY: 'auto', background: '#FAFAFA', display: 'flex', flexDirection: 'column' }}>
            <div style={{
                padding: '13px 28px', background: C.w, borderBottom: `1px solid ${C.n200}`,
                display: 'flex', alignItems: 'center', gap: 9, flexShrink: 0,
            }}>
                <span style={{ fontSize: 13, color: C.n400 }}>{breadcrumb || 'Dashboard'}</span>
                <span style={{ color: C.n300, fontSize: 13 }}>/</span>
                <span style={{ fontSize: 14, fontWeight: 700, color: C.n900 }}>{title}</span>
            </div>
            <div className="profile-content-shell" style={{ flex: 1, maxWidth: 780, width: '100%', alignSelf: 'center', boxSizing: 'border-box' }}>
                {children}
            </div>
        </div>
    );
}

function Card({ title, subtitle, children, action }) {
    return (
        <div style={{ background: C.w, border: `1px solid ${C.n200}`, borderRadius: 13, marginBottom: 18, overflow: 'hidden' }}>
            {(title || subtitle || action) && (
                <div style={{
                    padding: '14px 20px', borderBottom: `1px solid ${C.n100}`,
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                }}>
                    <div>
                        {title    && <div style={{ fontSize: 14, fontWeight: 700, color: C.n900 }}>{title}</div>}
                        {subtitle && <div style={{ fontSize: 12, color: C.n500, marginTop: 1 }}>{subtitle}</div>}
                    </div>
                    {action}
                </div>
            )}
            <div style={{ padding: '18px 20px' }}>{children}</div>
        </div>
    );
}

function Field({ label, value, type = 'text', onChange, readOnly, hint }) {
    return (
        <div style={{ marginBottom: 14 }}>
            <label style={{ display: 'block', fontSize: 12.5, fontWeight: 600, color: C.n600, marginBottom: 5 }}>
                {label}
            </label>
            <input
                type={type}
                value={value}
                onChange={onChange ? e => onChange(e.target.value) : undefined}
                readOnly={readOnly}
                style={{
                    width: '100%', padding: '8px 12px', boxSizing: 'border-box',
                    border: `1px solid ${C.n200}`, borderRadius: 8,
                    fontSize: 13.5, color: readOnly ? C.n500 : C.n900,
                    outline: 'none', background: readOnly ? C.n50 : 'transparent',
                    cursor: readOnly ? 'default' : 'text', fontFamily: 'inherit',
                }}
                onFocus={e => { if (!readOnly) e.target.style.borderColor = C.p400; }}
                onBlur={e => { e.target.style.borderColor = C.n200; }}
            />
            {hint && <div style={{ fontSize: 11, color: C.n400, marginTop: 4 }}>{hint}</div>}
        </div>
    );
}

function PasswordField({ label, value, onChange, hint }) {
    const [show, setShow] = useState(false);
    return (
        <div style={{ marginBottom: 14 }}>
            <label style={{ display: 'block', fontSize: 12.5, fontWeight: 600, color: C.n600, marginBottom: 5 }}>
                {label}
            </label>
            <div style={{ position: 'relative' }}>
                <input
                    type={show ? 'text' : 'password'}
                    value={value}
                    onChange={e => onChange(e.target.value)}
                    style={{
                        width: '100%', padding: '8px 38px 8px 12px', boxSizing: 'border-box',
                        border: `1px solid ${C.n200}`, borderRadius: 8,
                        fontSize: 13.5, color: C.n900,
                        outline: 'none', background: 'transparent', fontFamily: 'inherit',
                    }}
                    onFocus={e => { e.target.style.borderColor = C.p400; }}
                    onBlur={e => { e.target.style.borderColor = C.n200; }}
                />
                <button
                    type="button"
                    onClick={() => setShow(s => !s)}
                    style={{
                        position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)',
                        background: 'none', border: 'none', cursor: 'pointer', padding: 2,
                        display: 'flex', alignItems: 'center', color: C.n400,
                    }}
                >
                    {show ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
            </div>
            {hint && <div style={{ fontSize: 11, color: C.n400, marginTop: 4 }}>{hint}</div>}
        </div>
    );
}

function SaveButton({ onClick, saved, loading, disabled, label = 'Save changes' }) {
    const inactive = disabled || loading;
    return (
        <button
            type="button"
            onClick={onClick}
            disabled={inactive}
            style={{
                display: 'flex', alignItems: 'center', gap: 6,
                padding: '8px 18px',
                background: inactive ? C.n300 : C.p600,
                color: C.w, border: 'none', borderRadius: 8,
                fontSize: 13, fontWeight: 600,
                cursor: inactive ? 'default' : 'pointer',
                transition: 'background .15s', fontFamily: 'inherit',
            }}
        >
            {loading && (
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2"
                    style={{ animation: 'profilespin 1s linear infinite' }}>
                    <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                </svg>
            )}
            {!loading && saved && <Check size={13} color="white" />}
            {loading ? 'Saving…' : saved ? 'Saved!' : label}
        </button>
    );
}

// ── Profile page ──────────────────────────────────────────────────────────────

export default function Profile() {
    const [user, setUser]           = useState(null);
    const [isLoading, setIsLoading] = useState(true);

    // Personal info
    const [firstName, setFirstName] = useState('');
    const [lastName,  setLastName]  = useState('');
    const [email,     setEmail]     = useState('');
    const [infoSaving, setInfoSaving] = useState(false);
    const [infoSaved,  setInfoSaved]  = useState(false);

    // Password
    const [oldPw,    setOldPw]    = useState('');
    const [newPw,    setNewPw]    = useState('');
    const [pwSaving, setPwSaving] = useState(false);
    const [pwSaved,  setPwSaved]  = useState(false);

    // Avatar
    const [avatarUploading, setAvatarUploading] = useState(false);
    const avatarInputRef = useRef();

    // ── Load current user ────────────────────────────────────────────────────
    useEffect(() => {
        Auth.currentAuthenticatedUser({ bypassCache: true })
            .then(u => {
                setUser(u);
                setFirstName(u.attributes.given_name  || '');
                setLastName( u.attributes.family_name || '');
                setEmail(    u.attributes.email       || '');
            })
            .catch(err => { toast.error('Something went wrong.'); Sentry.captureException(err); })
            .finally(() => setIsLoading(false));
    }, []);

    // ── Save personal info ───────────────────────────────────────────────────
    const saveInfo = async () => {
        if (!user) return;
        setInfoSaving(true);
        try {
            await Auth.updateUserAttributes(user, {
                given_name:  firstName,
                family_name: lastName,
            });
            toast.success('Profile updated successfully!');
            setInfoSaved(true);
            setTimeout(() => setInfoSaved(false), 2500);
        } catch (err) {
            toast.error('Something went wrong updating your profile.');
            Sentry.captureException(err);
        } finally {
            setInfoSaving(false);
        }
    };

    // ── Change password ──────────────────────────────────────────────────────
    const savePw = async () => {
        if (!oldPw || newPw.length < 8) return;
        setPwSaving(true);
        try {
            const u = await Auth.currentAuthenticatedUser();
            await Auth.changePassword(u, oldPw, newPw);
            toast.success('Password changed successfully.');
            setPwSaved(true);
            setOldPw('');
            setNewPw('');
            setTimeout(() => setPwSaved(false), 2500);
        } catch (err) {
            toast.error(err.message);
            Sentry.captureException(err);
        } finally {
            setPwSaving(false);
        }
    };

    // ── Avatar upload ────────────────────────────────────────────────────────
    const onAvatarFile = async (e) => {
        const file = e.target.files[0];
        if (!file || !user) return;
        setAvatarUploading(true);
        try {
            const signedResponse = await API.get('main', '/avatar/signed-url', {
                queryStringParameters: { file_name: file.name, content_type: file.type },
            });
            await fetch(signedResponse.data.signed_url, { method: 'PUT', body: file, redirect: 'follow' });
            await Auth.updateUserAttributes(user, { 'custom:avatar': signedResponse.data.url });
            const updated = await Auth.currentAuthenticatedUser({ bypassCache: true });
            setUser(updated);
            toast.success('Avatar updated successfully!');
        } catch (err) {
            toast.error('Something went wrong updating your avatar.');
            Sentry.captureException(err);
        } finally {
            setAvatarUploading(false);
        }
    };

    // ── Loading skeleton ─────────────────────────────────────────────────────
    if (isLoading) {
        return <ProfileSkeleton />;
    }

    const avatarUrl = user?.attributes?.['custom:avatar'];
    const initials  = (firstName[0] || email[0] || '?').toUpperCase();

    return (
        <PageShell title="Profile" breadcrumb="Settings">
            <style>{`@keyframes profilespin { from { transform: rotate(0deg) } to { transform: rotate(360deg) } }`}</style>

            {/* ── Avatar card ── */}
            <div style={{
                background: C.w, border: `1px solid ${C.n200}`, borderRadius: 13,
                padding: '20px', marginBottom: 18,
                display: 'flex', alignItems: 'center', gap: 16,
            }}>
                {/* Avatar circle */}
                <div style={{ position: 'relative', flexShrink: 0 }}>
                    {avatarUrl ? (
                        <img
                            src={encodeURI(avatarUrl)}
                            alt="avatar"
                            style={{ width: 64, height: 64, borderRadius: '50%', objectFit: 'cover' }}
                        />
                    ) : (
                        <div style={{
                            width: 64, height: 64, borderRadius: '50%',
                            background: `linear-gradient(135deg,${C.p500},${C.v500})`,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontSize: 24, fontWeight: 800, color: C.w,
                        }}>
                            {initials}
                        </div>
                    )}
                    {avatarUploading && (
                        <div style={{
                            position: 'absolute', inset: 0, borderRadius: '50%',
                            background: 'rgba(0,0,0,.45)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2"
                                style={{ animation: 'profilespin 1s linear infinite' }}>
                                <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                            </svg>
                        </div>
                    )}
                </div>

                {/* Name / email / badges + Change avatar button */}
                <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 17, fontWeight: 700, color: C.n900, marginBottom: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {firstName}{lastName ? ' ' + lastName : ''}
                    </div>
                    <div style={{ fontSize: 13, color: C.n500, marginBottom: 8, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{email}</div>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
                        <div style={{ display: 'flex', gap: 6 }}>
                            <span style={{ padding: '2px 10px', background: C.p50,  color: C.p600, borderRadius: 999, fontSize: 11, fontWeight: 600 }}>
                                Starter Plan
                            </span>
                            <span style={{ padding: '2px 10px', background: C.g50, color: C.g600, borderRadius: 999, fontSize: 11, fontWeight: 600 }}>
                                ● Active
                            </span>
                        </div>
                        {/* Change avatar button — lives inside the info column so it never overflows */}
                        <input ref={avatarInputRef} type="file" accept="image/*" onChange={onAvatarFile} style={{ display: 'none' }} />
                        <button
                            type="button"
                            onClick={() => avatarInputRef.current.click()}
                            style={{
                                padding: '7px 14px', background: C.n100, border: 'none',
                                borderRadius: 8, fontSize: 12, fontWeight: 600, color: C.n600,
                                cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap', flexShrink: 0,
                            }}
                        >
                            Change avatar
                        </button>
                    </div>
                </div>
            </div>

            {/* ── Personal Information ── */}
            <Card title="Personal Information" subtitle="Update your name and contact details">
                <div className="profile-info-grid" style={{ marginBottom: 2 }}>
                    <Field label="First Name" value={firstName} onChange={setFirstName} />
                    <Field label="Last Name"  value={lastName}  onChange={setLastName}  />
                </div>
                <Field
                    label="Email Address"
                    value={email}
                    type="email"
                    readOnly
                    hint="Contact support to change your email address"
                />
                <SaveButton onClick={saveInfo} saved={infoSaved} loading={infoSaving} />
            </Card>

            {/* ── Change Password ── */}
            <Card title="Change Password" subtitle="Use a strong password of at least 8 characters">
                <PasswordField label="Current password" value={oldPw} onChange={setOldPw} />
                <PasswordField label="New password"     value={newPw} onChange={setNewPw} hint="Minimum 8 characters" />
                <SaveButton
                    onClick={savePw}
                    saved={pwSaved}
                    loading={pwSaving}
                    disabled={!oldPw || newPw.length < 8}
                    label="Update password"
                />
            </Card>

        </PageShell>
    );
}
